'use client'

import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { inputCls } from '@qr-front/ui/components/Input'
import {
	InputField,
	type TInputFieldProps,
} from '@qr-front/ui/components/InputField'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectIcon,
	SelectTrigger,
	SelectValue,
} from '@qr-front/ui/components/Select'
import { cn, EitherOr } from '@qr-front/ui/utils'
import { PrimitiveAtom } from 'jotai'
import { get as radashGet } from 'radash'
import {
	ChangeEvent,
	FocusEvent,
	FocusEventHandler,
	useEffect,
	useRef,
} from 'react'
import {
	FieldError,
	FieldPath,
	FieldPathValue,
	UseFormReturn,
	useFormState,
} from 'react-hook-form'

import { getStore } from '@/store'

import { posthogClient } from '../../../lib/analytics/posthog'
import { getBuilderSessionId, qrTypeAtom } from '../generatorState'

// MYQRCODE-10778 AC#14 — field_name is an identifier only, never carries the
// value the user typed. RHF's error `type` maps to the registry's two
// field-level error_reason values; everything but 'required' is a format
// problem from the field's own validation rule (max length, pattern, etc.).
const toErrorReason = (type: FieldError['type'] | undefined) =>
	type === 'required' ? 'required_empty' : 'invalid_format'

const captureFieldFilled = (fieldName: string, fieldGroup?: string) => {
	posthogClient.capture('builder_fill_field', {
		builder_session_id: getBuilderSessionId(),
		field_name: fieldName,
		field_group: fieldGroup,
		qr_type: getStore().get(qrTypeAtom),
		section: 'wizard_content',
	})
}

const captureValidationError = (
	fieldName: string,
	type: FieldError['type'] | undefined,
) => {
	posthogClient.capture('builder_show_validation_error', {
		builder_session_id: getBuilderSessionId(),
		field_name: fieldName,
		error_reason: toErrorReason(type),
		step_index: 2,
		qr_type: getStore().get(qrTypeAtom),
	})
}

type UseRhcFieldPropsOptions<
	F extends object,
	P extends FieldPath<F>,
	E extends HTMLElement = HTMLInputElement,
> = {
	form: UseFormReturn<F>
	path: P
	onChange?: (event: ChangeEvent<E>) => void
	onChangeTransform?: (event: ChangeEvent<E>) => ChangeEvent<E>
	onBlur?: FocusEventHandler<E>
	// MYQRCODE-10778 AC#14 — omit on fields not yet in the registry; only
	// fields passing a fieldName capture builder_fill_field/validation_error.
	fieldName?: string
	fieldGroup?: string
} & EitherOr<
	{ atom: PrimitiveAtom<FieldPathValue<F, P>> },
	{
		value: FieldPathValue<F, P>
		setValue: (value: FieldPathValue<F, P>) => void
	}
>

export const useRhcFieldProps = <
	E extends HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
	F extends object,
	P extends FieldPath<F>,
>({
	form,
	path,
	atom,
	value,
	setValue,
	onChange,
	onChangeTransform,
	onBlur,
	fieldName,
	fieldGroup,
}: UseRhcFieldPropsOptions<F, P, E>) => {
	const { register, setValue: setFormValue, control } = form

	useEffect(() => {
		let unsubscribe = () => {}
		if (atom) {
			unsubscribe = getStore().sub(atom, () => {
				const newValue = getStore().get(atom)
				setFormValue(path, newValue)
			})
		}
		return unsubscribe
	}, [atom, path, setFormValue])

	useEffect(() => {
		if (!atom) {
			setFormValue(path, value)
		}
	}, [atom, path, setFormValue, value])

	const {
		onChange: rhcOnChange,
		onBlur: rhcOnBlur,
		...rhcProps
	} = register(path)

	const { errors } = useFormState({ control, name: path })
	const fieldError = radashGet<FieldError | undefined>(errors, path)
	const errorMessage = fieldError?.message

	const hadErrorRef = useRef(false)
	useEffect(() => {
		if (fieldName && fieldError && !hadErrorRef.current) {
			captureValidationError(fieldName, fieldError.type)
		}
		hadErrorRef.current = Boolean(fieldError)
	}, [fieldError, fieldName])

	return {
		inputProps: {
			...rhcProps,
			onChange: (event: ChangeEvent<E>) => {
				const transformedEvent = onChangeTransform?.(event) || event
				const newValue = transformedEvent.currentTarget
					.value as FieldPathValue<F, P>
				if (atom) {
					getStore().set(atom, newValue)
				} else {
					setValue(newValue)
				}
				onChange?.(transformedEvent)
				rhcOnChange(transformedEvent)
			},
			onBlur: (event: FocusEvent<E, Element>) => {
				onBlur?.(event)
				rhcOnBlur(event)
				if (fieldName && (event.target as HTMLInputElement).value) {
					captureFieldFilled(fieldName, fieldGroup)
				}
			},
		},
		error: errorMessage,
	}
}

type TRhcFieldProps<F extends object, P extends FieldPath<F>> = Omit<
	TInputFieldProps,
	'form'
> &
	UseRhcFieldPropsOptions<F, P> & {
		deselectAndFocus?: boolean
	}

export const RhcField = <F extends object, P extends FieldPath<F>>({
	form,
	path,
	atom,
	value,
	setValue,
	onChange,
	onChangeTransform,
	onBlur,
	fieldName,
	fieldGroup,
	deselectAndFocus,
	...props
}: TRhcFieldProps<F, P>) => {
	const inputFieldProps: TInputFieldProps = { ...props } as TInputFieldProps

	const normalizeId = path.replace(/\./g, '__')

	const { inputProps, error } = useRhcFieldProps<HTMLInputElement, F, P>(
		atom
			? {
					form,
					path,
					atom,
					onChange,
					onChangeTransform,
					onBlur,
					fieldName,
					fieldGroup,
				}
			: {
					form,
					path,
					value,
					setValue,
					onChange,
					onChangeTransform,
					onBlur,
					fieldName,
					fieldGroup,
				},
	)

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		if (deselectAndFocus) {
			const input = e.currentTarget
			const length = input.value.length
			setTimeout(() => {
				input.setSelectionRange(length, length)
				input.focus()
			}, 0)
		}

		inputFieldProps.onFocus?.(e)
	}

	return (
		<InputField
			id={normalizeId}
			{...inputFieldProps}
			{...inputProps}
			onFocus={handleFocus}
			error={inputFieldProps.error || error}
		/>
	)
}

type TRhcSelectFieldProps<
	F extends object,
	P extends FieldPath<F>,
> = UseRhcFieldPropsOptions<F, P> & {
	children: React.ReactNode
	triggerClassName?: string
	onChange?: (value: string) => void
	form?: UseFormReturn<F>
	path?: P
} & EitherOr<
		{ atom: PrimitiveAtom<FieldPathValue<F, P>> },
		{
			value: FieldPathValue<F, P>
			setValue: (value: FieldPathValue<F, P>) => void
		}
	>

export const RhcSelectField = <F extends object, P extends FieldPath<F>>({
	form,
	path,
	atom,
	value,
	children,
	triggerClassName,
	setValue,
	onChange,
	onChangeTransform,
	fieldName,
	fieldGroup,
	...props
}: TRhcSelectFieldProps<F, P>) => {
	const { inputProps, error } = useRhcFieldProps<HTMLSelectElement, F, P>({
		form,
		path,
		fieldName,
		fieldGroup,
		...(atom
			? {
					atom,
					onChange: onChange as
						| ((
								event: React.ChangeEvent<HTMLSelectElement>,
						  ) => void)
						| undefined,
					onChangeTransform: onChangeTransform as
						| ((
								event: React.ChangeEvent<HTMLSelectElement>,
						  ) => React.ChangeEvent<HTMLSelectElement>)
						| undefined,
				}
			: {
					value,
					setValue,
					onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
						onChange?.(event.currentTarget.value)
					},
				}),
	})

	return (
		<Select
			value={form.watch(path)}
			onValueChange={(val) => {
				onChange?.(val)
				const syntheticEvent = {
					currentTarget: {
						value: val,
						name: path,
					},
					target: {
						value: val,
						name: path,
					},
				} as unknown as React.ChangeEvent<HTMLSelectElement>
				inputProps.onChange(syntheticEvent)
			}}
			{...props}
		>
			<SelectTrigger
				className={cn(
					'group/select relative mt-2 w-full text-left',
					inputCls(),
					triggerClassName,
				)}
			>
				<SelectValue />
				<SelectIcon className="absolute right-3 top-1/2 -translate-y-1/2">
					<LineChevronDownSmall
						width={16}
						height={16}
						className="text-dark-grey transition-transform duration-300 group-data-[state=closed]/select:rotate-0 group-data-[state=open]/select:rotate-180"
					/>
				</SelectIcon>
			</SelectTrigger>

			<SelectContent
				sideOffset={-4}
				position="popper"
				className="z-10 w-[var(--radix-select-trigger-width)]"
			>
				<SelectGroup className="mt-1 w-full rounded-8 border border-solid border-border-grey bg-white p-4">
					{children}
				</SelectGroup>
			</SelectContent>

			{error && <p className="text-sm text-red-600 mt-1">{error}</p>}
		</Select>
	)
}
